import { Controller } from "@hotwired/stimulus"

// MS54 #3062 §11.6 -- "flash" Stimulus controller.
//
// Replaces the global, non-Stimulus app/javascript/flash_messages.js script,
// which applied a single severity-blind auto-dismiss timer to EVERY flash
// message regardless of severity (a WCAG 2.2.1 Timing Adjustable violation
// for danger/error messages) and never fired at all for turbo-stream-rendered
// flashes, since turbo-stream prepend actions don't dispatch the
// DOMContentLoaded/turbo:load/turbo:render events the old script listened
// for.
//
// data-flash-sticky-value="true"  -> danger/error severities: NO auto-dismiss
//   timer at all -- the user must dismiss manually via the existing
//   .btn-close button (Bootstrap's own data-bs-dismiss handles that already,
//   this controller does not duplicate it).
// data-flash-sticky-value="false" -> success/info/notice/warning: a pausable
//   auto-dismiss timer that pauses on pointerenter/focusin and resumes on
//   pointerleave/focusout, so a user reading or focused inside the message
//   never has it disappear out from under them (WCAG 2.2.1 Timing Adjustable
//   / 2.2.2 Pause, Stop, Hide).
export default class extends Controller {
  static values = { sticky: Boolean, delay: { type: Number, default: 5000 } }

  connect() {
    this.timer = null
    this.remaining = this.dismissDelay()
    this.startedAt = null

    this.pause = this.pause.bind(this)
    this.resume = this.resume.bind(this)
    this.element.addEventListener("pointerenter", this.pause)
    this.element.addEventListener("focusin", this.pause)
    this.element.addEventListener("pointerleave", this.resume)
    this.element.addEventListener("focusout", this.resume)

    if (!this.stickyValue) this.start()
  }

  disconnect() {
    this.clear()

    this.element.removeEventListener("pointerenter", this.pause)
    this.element.removeEventListener("focusin", this.pause)
    this.element.removeEventListener("pointerleave", this.resume)
    this.element.removeEventListener("focusout", this.resume)
  }

  // Honors the legacy data-auto-dismiss attribute (still rendered by
  // _flash_messages.html.erb: 3000ms in test / 5000ms elsewhere) when
  // present; falls back to the Stimulus delay value (default 5000ms) for
  // turbo-stream render sites that don't set it.
  dismissDelay() {
    const legacy = parseInt(this.element.dataset.autoDismiss, 10)
    return Number.isFinite(legacy) ? legacy : this.delayValue
  }

  start() {
    this.startedAt = Date.now()
    this.timer = setTimeout(() => this.dismiss(), this.remaining)
  }

  pause() {
    if (this.stickyValue || !this.timer) return

    clearTimeout(this.timer)
    this.timer = null
    this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt))
  }

  resume() {
    if (this.stickyValue || this.timer || this.remaining <= 0) return

    this.start()
  }

  clear() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  dismiss() {
    this.clear()

    const element = this.element
    element.style.transition = "opacity 0.15s ease-out, transform 0.15s ease-out"
    element.style.opacity = "0"
    element.style.transform = "translateY(-20px)"

    setTimeout(() => {
      if (element.parentNode) element.parentNode.removeChild(element)
    }, 150)
  }
}
